import { reactive, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useUserStore } from '@/stores/userStore';
import { useRouter, type RouteLocationNormalized } from 'vue-router';
import { addToast } from './useToasts';
import { useI18n } from 'vue-i18n';
import { resetCustomerIo } from '@/plugins/customerio';
import { type ApiV1 } from '@teamworksdev/influencer/core';
import { isAdminRole } from '@/utils/users';

interface UseImpersonationAuthOptions {
	onImpersonationSuccess?: () => void;
	onImpersonationError?: () => void;
}

export const isImpersonationLoginRoute = (route: RouteLocationNormalized) => route.name === 'LoginImpersonate' && route.query.acting_as;

export function useImpersonationAuth(options?: UseImpersonationAuthOptions) {
	const authStore = useAuthStore();
	const userStore = useUserStore();
	const router = useRouter();
	const { t } = useI18n();

	const defaultImpersonationTokenRequest: ApiV1.ImpersonationAuthRequest = {
		user: '',
	};
	const impersonationTokenRequest: ApiV1.ImpersonationAuthRequest = reactive({ ...defaultImpersonationTokenRequest });

	function reset() {
		Object.assign(impersonationTokenRequest, { ...defaultImpersonationTokenRequest });
	}

	function handleSuccess() {
		if (options?.onImpersonationSuccess !== undefined) {
			options.onImpersonationSuccess();
		}
	}

	function handleError() {
		reset();
		if (options?.onImpersonationError !== undefined) {
			options.onImpersonationError();
		}
		addToast({
			title: t('inflcrErrorCodes.accessImpersonationUserUnconfirmed.title'),
			description: t('inflcrErrorCodes.accessImpersonationUserUnconfirmed.message'),
			type: 'error',
		});
	}

	const canImpersonate = (profile: ApiV1.MinimalUserProfile) => {
		return isAdminRole(profile.role_id);
	};

	function waitForTermsAcceptance(): Promise<void> {
		return new Promise((resolve, reject) => {
			const unwatch = watch(
				[() => userStore.user, () => userStore.hasUserAcceptedLatestTermsOfUse],
				([user, hasAccepted]) => {
					if (!user) {
						unwatch();
						reject(new Error('User logged out'));
						return;
					}
					if (hasAccepted) {
						unwatch();
						resolve();
					}
				},
				{ immediate: true },
			);
		});
	}

	async function logInImpersonation() {
		try {
			await userStore.setUser(true);

			if (!userStore.hasUserAcceptedLatestTermsOfUse) await waitForTermsAcceptance();

			// Ensure profiles are loaded for impersonator
			await userStore.ensureUserProfiles(true);

			let authorizedProfileUuid: string | undefined = undefined;

			const profile = userStore.userProfiles![0];
			authorizedProfileUuid = profile?.uuid;

			const hasMultipleProfiles = userStore.userProfiles?.length !== 1;
			if (hasMultipleProfiles) {
				const profile = userStore.userProfiles!.find(canImpersonate);
				authorizedProfileUuid = profile?.uuid || authorizedProfileUuid;
			}
			await userStore.setProfile(authorizedProfileUuid, true);
			await authStore.getTokenImpersonation(impersonationTokenRequest);
			await userStore.setImpersonatedUser();
			handleSuccess();
		} catch (e) {
			if (e instanceof Error && e.message === 'User logged out') {
				return;
			}
			console.error(e);
			handleError();
		}
	}

	async function logOutImpersonation(redirect: boolean = true) {
		const token = authStore.impersonationToken;
		if (token) await authStore.removeImpersonationToken(token.uuid);
		userStore.unsetUser();
		resetCustomerIo();
		if (redirect) await router.push({ name: 'ProfileChooserIndex' });
	}

	return {
		impersonationTokenRequest,
		logInImpersonation,
		logOutImpersonation,
	};
}
